import React, { useRef, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  InputBase,
} from "@material-ui/core";
import { Clear, Search } from "@material-ui/icons";
import { isoToName } from "../../config/dialCodes";

const CONVERSION_FACTOR = 100000;

const COL_WIDTHS = ["18%", "21%", "15%", "15%", "15%", "16%"];

function ColGroup() {
  return (
    <colgroup>
      {COL_WIDTHS.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
}

function formatNum(n) {
  const rounded = Math.round(n / 1000) * 1000;
  if (rounded >= 1000000) {
    const val = rounded / 1000000;
    return `${val % 1 === 0 ? val : val.toFixed(1)}M`;
  }
  if (rounded >= 1000) return `${rounded / 1000}K`;
  return `${rounded}`;
}

function formatTierRange(tier) {
  if (tier.max) return `${formatNum(tier.min)} – ${formatNum(tier.max)}`;
  return `${formatNum(tier.min)}+`;
}

// Wraps the matched portion of `text` in a yellow highlight span.
function HighlightText({ text, query }) {
  if (!query) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} style={{ backgroundColor: "#ffe082", borderRadius: 2, padding: "0 1px" }}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

function WaCallPricingTable({ open, onClose, waCallPricing }) {
  const bodyRef = useRef(null);
  const [scrollbarW, setScrollbarW] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (open && bodyRef.current) {
      setScrollbarW(bodyRef.current.offsetWidth - bodyRef.current.clientWidth);
    }
  }, [open]);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  if (!open) return null;

  const query = searchTerm.trim().toLowerCase();
  const countries = Object.keys(waCallPricing || {})
    .filter((iso) => {
      if (!query) return true;
      const name = (isoToName[iso] || iso).toLowerCase();
      return name.includes(query) || iso.toLowerCase().includes(query);
    })
    .sort((a, b) => (isoToName[a] || a).localeCompare(isoToName[b] || b));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { minHeight: "85vh" } }}
    >
      {/* ── Fixed: heading ── */}
      <DialogTitle>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h3" style={{ fontWeight: 700 }}>
            AiSensy WhatsApp Call Pricing Family
          </Typography>
          <IconButton color="primary" size="small" onClick={onClose}>
            <Clear style={{ position: "fixed" }} />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Fixed: description ── */}
      <Box px={3} pb={1}>
        <Typography variant="body2" style={{ color: "#666" }}>
          Fixed per-minute rates for WhatsApp calls, broken down by country and
          volume tier. User Initiated calls are always free.
        </Typography>
      </Box>

      {/* ── Fixed: search — below description, with breathing room ── */}
      <Box px={3} pt={2} pb={2}>
        <Box style={styles.searchBar}>
          <Search style={{ color: "#461486", fontSize: 18, marginRight: 8, flexShrink: 0 }} />
          <InputBase
            placeholder="Search by country name or code…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            style={{ fontSize: 14 }}
          />
          {searchTerm && (
            <IconButton size="small" onClick={() => setSearchTerm("")} style={{ padding: 2 }}>
              <Clear style={{ fontSize: 16, color: "#999" }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <DialogContent style={{ padding: "0 24px 24px", overflow: "hidden" }}>
        {/* Fixed: header table */}
        <div style={{ paddingRight: scrollbarW }}>
          <table style={{ ...styles.table, borderBottom: "none" }}>
            <ColGroup />
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...styles.th, top: 0 }}>
                  Country
                </th>
                <th rowSpan={2} style={styles.th}>
                  Tier (minutes/month)
                </th>
                <th colSpan={2} style={{ ...styles.th, borderBottom: "none" }}>
                  User Initiated
                </th>
                <th colSpan={2} style={{ ...styles.th, borderBottom: "none" }}>
                  Business Initiated
                </th>
              </tr>
              <tr>
                <th style={styles.thSub}>USD Pricing</th>
                <th style={styles.thSub}>INR Pricing</th>
                <th style={styles.thSub}>USD Pricing</th>
                <th style={styles.thSub}>INR Pricing</th>
              </tr>
            </thead>
          </table>
        </div>

        {/* Scrollable: body rows only */}
        <div
          ref={bodyRef}
          style={{ maxHeight: "58vh", overflowY: "scroll", overflowX: "hidden" }}
        >
          <table style={styles.table}>
            <ColGroup />
            <tbody>
              {countries.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ ...styles.td, textAlign: "center", color: "#999", padding: "32px 16px" }}
                  >
                    {searchTerm
                      ? `No countries match "${searchTerm}"`
                      : "No pricing data available"}
                  </td>
                </tr>
              ) : (
                countries.map((isoCode) => {
                  const tiers = waCallPricing[isoCode];
                  const tierKeys = Object.keys(tiers);
                  const countryName = isoToName[isoCode] || isoCode;

                  return tierKeys.map((tierKey, tierIndex) => {
                    const tier = tiers[tierKey];
                    const isFirstTier = tierIndex === 0;
                    const isLastTier = tierIndex === tierKeys.length - 1;

                    const borderTop = isFirstTier ? "2px solid #444" : "1px solid #ddd";
                    const borderBottom = isLastTier ? "2px solid #444" : "1px solid #ddd";
                    const cellStyle = { ...styles.td, borderTop, borderBottom };

                    return (
                      <tr key={`${isoCode}-${tierKey}`}>
                        {isFirstTier && (
                          <td
                            rowSpan={tierKeys.length}
                            style={{
                              ...styles.td,
                              ...styles.tdCountry,
                              borderTop: "2px solid #444",
                              borderBottom: "2px solid #444",
                            }}
                          >
                            <Typography style={{ fontWeight: 700, fontSize: 14 }}>
                              <HighlightText text={countryName} query={query} />
                            </Typography>
                            <Typography variant="caption" style={{ color: "#888" }}>
                              <HighlightText text={isoCode} query={query} />
                            </Typography>
                          </td>
                        )}
                        <td style={cellStyle}>
                          <Typography style={{ fontWeight: 600, display: "block", fontSize: 13 }}>
                            {tierKey.replace("tier", "Tier ")}
                          </Typography>
                          <Typography style={{ color: "#666", fontSize: 12 }}>
                            {formatTierRange(tier)}
                          </Typography>
                        </td>
                        <td style={{ ...cellStyle, ...styles.tdZero }}>$0</td>
                        <td style={{ ...cellStyle, ...styles.tdZero }}>₹0</td>
                        <td style={cellStyle}>
                          ${(tier.rateUSD / CONVERSION_FACTOR).toFixed(5)}
                        </td>
                        <td style={cellStyle}>
                          ₹{(tier.rateINR / CONVERSION_FACTOR).toFixed(5)}
                        </td>
                      </tr>
                    );
                  });
                })
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const styles = {
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    fontSize: 14,
  },
  th: {
    backgroundColor: "#461486",
    color: "white",
    fontWeight: 700,
    padding: "11px 16px",
    textAlign: "center",
    border: "1px solid #5a1a9e",
    whiteSpace: "nowrap",
  },
  thSub: {
    backgroundColor: "#f3e8ff",
    color: "#461486",
    fontWeight: 700,
    padding: "9px 16px",
    textAlign: "center",
    border: "1px solid #c9a0f0",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 16px",
    borderLeft: "1px solid #ddd",
    borderRight: "1px solid #ddd",
    textAlign: "center",
    verticalAlign: "middle",
    fontSize: 14,
  },
  tdCountry: {
    textAlign: "left",
    verticalAlign: "middle",
  },
  tdZero: {
    color: "#aaa",
  },
  searchBar: {
    display: "flex",
    alignItems: "center",
    border: "1.5px solid #d0b3f0",
    borderRadius: 8,
    padding: "7px 12px",
    backgroundColor: "#faf5ff",
  },
};

export default WaCallPricingTable;
